import React, { memo, useEffect } from 'react'
import { colors, Hooks, Typography } from '@microfrontends/react-components'

import { Section } from './makeProfileSearchable.css'
import { TMakeProfileSearchableProps } from '../type'
import Attachments from './Attachments'
import OnlineProfile from './OnlineProfile'
import { EDevice, TProfileSetting, useStore } from '../../../../store'
import { useProfileSettingStore } from '../../../../store/ProfileSetting'
import { apiGetOpenToWork, apiGetRefresh } from '../services'
import { convertDateTime } from '../../../../helpers/date'

const MakeProfileSearchable = (props: TMakeProfileSearchableProps) => {
    const { trans } = props

    const { state } = useStore()
    const profileSettingStore = useProfileSettingStore()
    const settingState = profileSettingStore.state
    const disPatchSettingState = profileSettingStore.setState

    const { profileSettingInfo, user } = state
    const { resumeSetting, resumeSettingOption } = profileSettingInfo

    const initDataMakeSearchable = {
        data: {
            resumes: [...[resumeSetting.profileInfo], ...resumeSetting.attachInfo],
            profileSetting: profileSettingInfo.resumeSettingOption as Partial<TProfileSetting>,
            searchableStatus: profileSettingInfo.searchableStatus as 0 | 1,
            unContactable: profileSettingInfo.unContactable  as 0 | 1
        },
        accessToken: user?.accessToken ?? '',
        langCode: state.langCode,
        baseMsUrl: process.env.NEXT_PUBLIC_MS_BASE_PATH ?? ''
    }

    const getRefresh = async () => {
        // Do not call apiGetRefresh when accessToken is not provided
        if (!user?.accessToken) {
            return
        }
        const response = await apiGetRefresh(user?.accessToken)

        if (response.meta?.status === 200) {
            const date = new Date()
            date.setDate(date.getDate() + 15)

            return {
                expiredDate: convertDateTime(response.data?.attributes?.expiredDate || date, 7),
                statusRefresh: response.data?.attributes?.type ?? 0
            }
        }
    }

    const getOpenToWork = async (): Promise<[boolean, {
        isOpenToWork: boolean;
        expiredOn: string
    } | null]> => {
        if (!user?.accessToken) {
            return [false, null]
        }
        const response = await apiGetOpenToWork(user?.accessToken)

        if (response.data && response.data.attributes) {
            return [true, {
                isOpenToWork: response.data?.attributes?.isOpenToWork === 1,
                expiredOn: response.data?.attributes?.openToWorkExpiredOn ?? ''
            }]
        }
        return [false, null]
    }

    // TODO: Refactor Hooks.useMakeSearchable
    // to return an object instead of an array for improved usability.
    const [_, _1, priorityResume, searchableResume] = Hooks.useMakeSearchable(initDataMakeSearchable)

    useEffect(() => {
        const { attachInfo, profileInfo } = profileSettingInfo.resumeSetting

        let currentSearchableResumeId = profileInfo.isSearchable === 1 ? profileInfo.id : (priorityResume?.id ?? 0)
        // Profile online is not searchable then maybe attach is searchable
        if (profileInfo.isSearchable === 0) {
            const filteredAttach = attachInfo.filter((item) => item.isSearchable === 1)
            if (filteredAttach.length > 0) {
                currentSearchableResumeId = filteredAttach[0].id
            }
        }

        if (currentSearchableResumeId === 0 && priorityResume?.id) {
            currentSearchableResumeId = priorityResume?.id
        }
        (async () => {
            const responseGetRefresh = await getRefresh()
            const [isOpenToWorkSuccess, responseOpenToWork] = await getOpenToWork()
            const newProfileSettingState = { ...settingState, resumeId: currentSearchableResumeId, ...responseGetRefresh }

            if (isOpenToWorkSuccess && responseOpenToWork !== null) {
                newProfileSettingState.isOpenToWork = responseOpenToWork.isOpenToWork
            }
            disPatchSettingState(newProfileSettingState)
        })()
    }, [profileSettingInfo, priorityResume])

    return (
        <Section>
            <Typography name='title' size='medium' color={colors.grey[20]}>
                {trans('Default searchable CV')}
            </Typography>
            <OnlineProfile trans={trans} searchableResume={searchableResume} />
            <Attachments trans={trans} searchableResume={searchableResume} />
        </Section>
    )
}

export default memo(MakeProfileSearchable)
